import React from 'react';
import { Zap, FileText, TrendingUp, DollarSign } from 'lucide-react';

/**
 * Quick Start Templates
 * Provides pre-filled templates for common scenarios
 */
export default function QuickStartTemplates({ onSelectTemplate }) {
  const templates = [
    {
      id: 'child-support-standard',
      name: 'Child Support - Standard',
      icon: FileText,
      description: 'Typical child support pricing with balanced margins',
      color: 'blue',
      data: {
        volumeAssumptions: {
          totalPayments: 1000, // $1M
          totalTransactions: 50000,
          achPercent: 14.5,
          ccPercent: 62,
          amexPercent: 8.5,
          debitPercent: 15
        },
        incumbentPricing: {
          tier1MaxAmount: 50,
          tier1MinFee: 1.00,
          tier2MaxAmount: 100,
          tier2MinFee: 1.50,
          tier3MaxAmount: 9999,
          tier3MinFee: 2.00,
          paymentRates: {
            ACH: { takeRate: 2.50, minFee: 1.00 },
            CC: { takeRate: 2.50, minFee: 1.00 },
            AMEX: { takeRate: 3.50, minFee: 1.50 },
            DEBIT: { takeRate: 2.00, minFee: 0.75 }
          }
        },
        proposedPricing: {
          tier1MaxAmount: 50,
          tier1MinFee: 0.95,
          tier2MaxAmount: 100,
          tier2MinFee: 1.45,
          tier3MaxAmount: 9999,
          tier3MinFee: 1.95,
          paymentRates: {
            ACH: { takeRate: 2.25, minFee: 0.95 },
            CC: { takeRate: 2.25, minFee: 0.95 },
            AMEX: { takeRate: 3.25, minFee: 1.45 },
            DEBIT: { takeRate: 1.85, minFee: 0.70 }
          }
        }
      }
    },
    {
      id: 'traffic-competitive',
      name: 'Traffic - Competitive',
      icon: TrendingUp,
      description: 'Aggressive pricing to win traffic/parking payments',
      color: 'green',
      data: {
        volumeAssumptions: {
          totalPayments: 500,
          totalTransactions: 30000,
          achPercent: 10,
          ccPercent: 65,
          amexPercent: 10,
          debitPercent: 15
        },
        incumbentPricing: {
          tier1MaxAmount: 50,
          tier1MinFee: 1.25,
          tier2MaxAmount: 100,
          tier2MinFee: 1.75,
          tier3MaxAmount: 9999,
          tier3MinFee: 2.25,
          paymentRates: {
            ACH: { takeRate: 2.75, minFee: 1.25 },
            CC: { takeRate: 2.75, minFee: 1.25 },
            AMEX: { takeRate: 3.75, minFee: 1.75 },
            DEBIT: { takeRate: 2.25, minFee: 0.95 }
          }
        },
        proposedPricing: {
          tier1MaxAmount: 50,
          tier1MinFee: 0.85,
          tier2MaxAmount: 100,
          tier2MinFee: 1.25,
          tier3MaxAmount: 9999,
          tier3MinFee: 1.75,
          paymentRates: {
            ACH: { takeRate: 2.00, minFee: 0.85 },
            CC: { takeRate: 2.00, minFee: 0.85 },
            AMEX: { takeRate: 3.00, minFee: 1.25 },
            DEBIT: { takeRate: 1.60, minFee: 0.60 }
          }
        }
      }
    },
    {
      id: 'premium-margins',
      name: 'Premium - High Margin',
      icon: DollarSign,
      description: 'Higher pricing for premium service or exclusive contracts',
      color: 'purple',
      data: {
        volumeAssumptions: {
          totalPayments: 1500,
          totalTransactions: 60000,
          achPercent: 20,
          ccPercent: 55,
          amexPercent: 12,
          debitPercent: 13
        },
        incumbentPricing: {
          tier1MaxAmount: 50,
          tier1MinFee: 1.00,
          tier2MaxAmount: 100,
          tier2MinFee: 1.50,
          tier3MaxAmount: 9999,
          tier3MinFee: 2.00,
          paymentRates: {
            ACH: { takeRate: 2.50, minFee: 1.00 },
            CC: { takeRate: 2.50, minFee: 1.00 },
            AMEX: { takeRate: 3.50, minFee: 1.50 },
            DEBIT: { takeRate: 2.00, minFee: 0.75 }
          }
        },
        proposedPricing: {
          tier1MaxAmount: 50,
          tier1MinFee: 1.15,
          tier2MaxAmount: 100,
          tier2MinFee: 1.65,
          tier3MaxAmount: 9999,
          tier3MinFee: 2.15,
          paymentRates: {
            ACH: { takeRate: 2.75, minFee: 1.15 },
            CC: { takeRate: 2.75, minFee: 1.15 },
            AMEX: { takeRate: 3.75, minFee: 1.65 },
            DEBIT: { takeRate: 2.15, minFee: 0.85 }
          }
        }
      }
    }
  ];

  const colorClasses = {
    blue: 'from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700',
    green: 'from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700',
    purple: 'from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700'
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="w-5 h-5 text-indigo-600" />
        <h3 className="text-lg font-semibold text-slate-900">Quick Start Templates</h3>
      </div>
      
      <p className="text-sm text-slate-600 mb-4">
        Skip the setup! Start with a pre-filled template and customize as needed.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {templates.map((template) => {
          const Icon = template.icon;
          return (
            <button
              key={template.id}
              onClick={() => onSelectTemplate(template.data)}
              className={`
                relative overflow-hidden rounded-lg p-4 text-left
                bg-gradient-to-br ${colorClasses[template.color]}
                text-white transition-all transform hover:scale-105 hover:shadow-lg
                group
              `}
            >
              {/* Icon */}
              <div className="absolute top-2 right-2 opacity-20 group-hover:opacity-30 transition-opacity">
                <Icon className="w-16 h-16" />
              </div>

              {/* Content */}
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                    <Icon className="w-4 h-4" />
                  </div>
                  <h4 className="font-semibold">{template.name}</h4>
                </div>
                <p className="text-sm text-white/90">{template.description}</p>
                
                <div className="mt-3 pt-3 border-t border-white/20">
                  <div className="text-xs font-medium">Click to apply →</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-xs text-amber-900">
          <strong>Note:</strong> Templates pre-fill Volume, Incumbent, and Proposed Pricing sections. 
          You'll still need to fill Client Info and Transaction Distribution.
        </p>
      </div>
    </div>
  );
}
